import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import ActionCreators from "../../actions";
import { setLyftReportData, setTuroReportData, setOtherReportData } from "../../utils/functions";
import { getCertificateFontDownloads } from "../../utils/certificateFontDownloads";
import "./certificateDownloadButton.css";
import moment from "moment";
import {
  PDFDownloadLink,
  Page,
  Text,
  View,
  Image,
  Document,
  StyleSheet,
  Font,
  Svg,
  Path,
} from "@react-pdf/renderer";

import kalamFont from "../../font/Kalam-Light.ttf";
import "../../font/registerPatrickHandAndCaveatPdf";
import {
  handDrawnCirclePath,
  HAND_DRAWN_CIRCLE_VIEWBOX,
} from "../../utils/handDrawnCircle";

Font.register({ family: "kalamFont", format: "truetype", src: kalamFont });

const LYFT_LOGO_URL =
  "https://res.cloudinary.com/dgjordf6e/image/upload/v1742279338/image_22_kh5uqw.png";

const inspectionColumns = [
  [
    { number: 1, label: "Foot brakes", aliases: ["foot brake", "foot brakes", "brake"] },
    {
      number: 2,
      label: "Emergency brake (parking brake)",
      aliases: ["emergency brake", "parking brake"],
    },
    { number: 3, label: "Steering mechanism", aliases: ["steering"] },
    { number: 4, label: "Windshield", aliases: ["windshield"] },
    {
      number: 5,
      label: "Rear window and other glass",
      aliases: ["rear window", "other glass"],
    },
    { number: 6, label: "Windshield wipers", aliases: ["wiper", "wipers"] },
    { number: 7, label: "Headlights", aliases: ["headlight", "headlights"] },
    { number: 8, label: "Tail lights", aliases: ["tail light", "tail lights"] },
    {
      number: 9,
      label: "Turn indicator lights",
      aliases: ["turn indicator", "turn signal", "indicator lights"],
    },
    { number: 10, label: "Stop lights", aliases: ["stop light", "brake light"] },
    {
      number: 11,
      label: "Front seat adjustment",
      aliases: ["front seat adjustment", "seat adjustment"],
    },
    {
      number: 12,
      label: "Doors (open, close, lock)",
      aliases: ["door", "doors"],
    },
  ],
  [
    { number: 13, label: "Horn", aliases: ["horn"] },
    { number: 14, label: "Speedometer", aliases: ["speedometer"] },
    { number: 15, label: "Bumpers", aliases: ["bumper", "bumpers"] },
    {
      number: 16,
      label: "Muffler and exhaust system",
      aliases: ["muffler", "exhaust"],
    },
    {
      number: 17,
      label: "Tires, incl. tread depth",
      aliases: ["tire", "tires", "tread depth"],
      subRows: [
        { label: "Left front", aliases: ["tire", "tires", "tread depth"] },
        { label: "Right front", aliases: ["tire", "tires", "tread depth"] },
        { label: "Left rear", aliases: ["tire", "tires", "tread depth"] },
        { label: "Right rear", aliases: ["tire", "tires", "tread depth"] },
      ],
    },
    {
      number: 18,
      label: "Interior and exterior rear view mirrors",
      aliases: ["rear view mirror", "rearview mirror", "mirrors"],
    },
    {
      number: 19,
      label: "Safety belts for driver and passenger(s)",
      aliases: ["safety belt", "seat belt"],
    },
  ],
];

const normalize = (value = "") =>
  String(value)
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const getFullName = (data = {}) =>
  `${data?.Username || ""} ${data?.UserlastName || ""}`.trim();

const getInspectorName = (data = {}) =>
  `${data?.Inspectorname || ""} ${data?.InspectorlastName || ""}`.trim();

const formatDate = (value) =>
  value ? moment(new Date(value)).format("MM/DD/YYYY") : "";

const getCheckStatus = (checks = [], aliases = []) => {
  const normalizedAliases = aliases.map(normalize);
  const match = checks.find((check) => {
    const name = normalize(check?.Check?.name || check?.name);
    if (!name) return false;
    return normalizedAliases.some(
      (alias) => name.includes(alias) || alias.includes(name)
    );
  });

  if (!match) return null;
  const status = String(match.status).toLowerCase();
  return match.status === true ||
    match.status === 1 ||
    status === "true" ||
    status === "1" ||
    status === "pass"
    ? "pass"
    : "fail";
};

const styles = StyleSheet.create({
  page: {
    backgroundColor: "#fff",
    color: "#111",
    fontFamily: "Helvetica",
    paddingBottom: 0,
  },
  header: {
    alignItems: "center",
    backgroundColor: "#000",
    color: "#fff",
    flexDirection: "row",
    height: 52,
    justifyContent: "space-between",
    paddingHorizontal: 40,
  },
  brand: {
    alignItems: "center",
    flexDirection: "row",
  },
  logo: {
    height: 20,
    objectFit: "contain",
    width: 50,
  },
  divider: {
    backgroundColor: "#fff",
    height: 22,
    marginHorizontal: 16,
    width: 1,
  },
  stateText: {
    color: "#fff",
    fontSize: 14,
    fontWeight: 700,
  },
  headerTitle: {
    color: "#fff",
    fontSize: 14,
    fontWeight: 700,
  },
  driverSection: {
    paddingHorizontal: 40,
    paddingTop: 12,
  },
  titleRow: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  driverTitle: {
    fontSize: 15,
    fontWeight: 800,
  },
  mandatory: {
    fontSize: 8.5,
    fontWeight: 800,
  },
  row: {
    flexDirection: "row",
    marginBottom: 8,
  },
  field: {
    marginRight: 14,
  },
  fieldValue: {
    borderBottomColor: "#777",
    borderBottomWidth: 1,
    fontSize: 9,
    minHeight: 13,
    paddingBottom: 2,
  },
  fieldLabel: {
    fontSize: 7,
    marginTop: 3,
    textTransform: "uppercase",
  },
  wHalf: {
    flex: 1,
  },
  wName: {
    width: 283,
  },
  wPlate: {
    width: 232,
  },
  wMake: {
    width: 232,
  },
  wModel: {
    flex: 1,
  },
  wYear: {
    width: 78,
  },
  wSignature: {
    width: 232,
  },
  wPhone: {
    width: 224,
  },
  noMargin: {
    marginRight: 0,
  },
  vinBoxes: {
    borderBottomColor: "#777",
    borderBottomWidth: 1,
    flexDirection: "row",
    height: 15,
  },
  vinBox: {
    alignItems: "center",
    borderLeftColor: "#777",
    borderLeftWidth: 1,
    flex: 1,
    justifyContent: "center",
  },
  vinChar: {
    fontSize: 9,
  },
  checklist: {
    paddingHorizontal: 40,
    paddingTop: 16,
  },
  checklistTitle: {
    fontSize: 15,
    fontWeight: 800,
    marginBottom: 8,
  },
  checklistGrid: {
    flexDirection: "row",
  },
  checkColumn: {
    flex: 1,
  },
  leftColumn: {
    marginRight: 34,
  },
  checkHeading: {
    alignItems: "center",
    color: "#777",
    flexDirection: "row",
    fontSize: 9,
    marginBottom: 4,
    textTransform: "uppercase",
  },
  pointHeading: {
    flex: 1,
  },
  statusHeading: {
    textAlign: "center",
    width: 36,
  },
  checkRow: {
    alignItems: "center",
    flexDirection: "row",
    minHeight: 16,
  },
  checkLabel: {
    flex: 1,
    flexDirection: "row",
  },
  checkNumber: {
    color: "#777",
    fontSize: 9,
    textAlign: "right",
    width: 20,
  },
  checkText: {
    fontSize: 9,
    fontWeight: 800,
    lineHeight: 1.15,
  },
  subText: {
    fontSize: 9,
    marginLeft: 20,
  },
  boxWrap: {
    alignItems: "center",
    width: 36,
  },
  box: {
    borderColor: "#777",
    borderWidth: 1,
    height: 10,
    position: "relative",
    width: 10,
  },
  tick: {
    height: 15,
    left: -2,
    position: "absolute",
    top: -6,
    width: 15,
  },
  resultBox: {
    alignItems: "center",
    borderColor: "#777",
    borderStyle: "dashed",
    borderWidth: 1,
    flexDirection: "row",
    marginTop: 26,
    minHeight: 48,
    paddingHorizontal: 14,
  },
  resultTitle: {
    flex: 1,
    fontSize: 10,
    fontWeight: 800,
  },
  resultCircleOne: {
    color: "#666",
    fontSize: 8.5,
    fontWeight: 400,
  },
  resultOption: {
    alignItems: "center",
    height: 30,
    justifyContent: "center",
    position: "relative",
    textAlign: "center",
    width: 52,
  },
  resultOptionText: {
    fontSize: 11,
    fontWeight: 800,
    textAlign: "center",
  },
  resultCircle: {
    height: 32,
    left: 17,
    position: "absolute",
    top: -2,
    width: 46,
  },
  inspectorSection: {
    backgroundColor: "#e8e3e3",
    marginHorizontal: 30,
    marginTop: 22,
    paddingBottom: 14,
    paddingHorizontal: 16,
    paddingTop: 14,
  },
  inspectorTitle: {
    fontSize: 11,
    fontWeight: 800,
    marginBottom: 14,
  },
  signatureImage: {
    height: 48,
    objectFit: "contain",
    width: 220,
  },
});

const Field = ({ label, value, style, handwritingStyle }) => (
  <View style={[styles.field, style].filter(Boolean)}>
    <Text style={[styles.fieldValue, handwritingStyle].filter(Boolean)}>
      {value || ""}
    </Text>
    <Text style={styles.fieldLabel}>{label}</Text>
  </View>
);

const VinField = ({ value, handwritingStyle }) => {
  const chars = String(value || "").slice(0, 17).split("");
  return (
    <View style={[styles.field, styles.wHalf, styles.noMargin].filter(Boolean)}>
      <View style={styles.vinBoxes}>
        {Array.from({ length: 17 }).map((_, index) => (
          <View style={styles.vinBox} key={index}>
            <Text style={[styles.vinChar, handwritingStyle].filter(Boolean)}>
              {chars[index] || ""}
            </Text>
          </View>
        ))}
      </View>
      <Text style={styles.fieldLabel}>VIN</Text>
    </View>
  );
};

const CheckBox = ({ checked }) => (
  <View style={styles.boxWrap}>
    <View style={styles.box}>
      {checked ? (
        <Svg style={styles.tick} viewBox="0 0 24 24">
          <Path
            d="M4 12.5C6.3 15.4 8.1 17.7 9.2 18.7C12.5 13.5 16.1 8.8 21 4.4"
            stroke="#111"
            strokeWidth={2.7}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </Svg>
      ) : null}
    </View>
  </View>
);

const InspectionItem = ({ item, checks }) => {
  const status = getCheckStatus(checks, item.aliases);
  return (
    <>
      <View style={styles.checkRow}>
        <View style={styles.checkLabel}>
          <Text style={styles.checkNumber}>{item.number}. </Text>
          <Text style={styles.checkText}>{item.label}</Text>
        </View>
        <CheckBox checked={status === "pass"} />
        <CheckBox checked={status === "fail"} />
      </View>
      {item.subRows?.map((subRow) => {
        const subStatus = getCheckStatus(checks, subRow.aliases);
        return (
          <View style={styles.checkRow} key={subRow.label}>
            <View style={styles.checkLabel}>
              <Text style={styles.subText}>{subRow.label}</Text>
            </View>
            <CheckBox checked={subStatus === "pass"} />
            <CheckBox checked={subStatus === "fail"} />
          </View>
        );
      })}
    </>
  );
};

const ResultOption = ({ selected, children }) => (
  <View style={styles.resultOption}>
    {selected ? (
      <Svg style={styles.resultCircle} viewBox={HAND_DRAWN_CIRCLE_VIEWBOX}>
        <Path
          d={handDrawnCirclePath}
          stroke="#111"
          strokeWidth={3}
          strokeLinecap="round"
          fill="none"
        />
      </Svg>
    ) : null}
    <Text style={styles.resultOptionText}>{children}</Text>
  </View>
);

function LyftCertificateArizonaDownLoad({
  vehicleChecksArray,
  certificateData = {},
  handwritingFont = "kalamFont",
  fileName = "Vehicle-certificate.pdf",
  linkLabel = "Download pdf",
}) {
  const handwritingStyle = { fontFamily: handwritingFont };
  const finalStatus = String(certificateData?.finalStatus || "").toLowerCase();

  return (
    <div className="App">
      <PDFDownloadLink
        document={
          <Document>
            <Page size="A4" style={styles.page} wrap={false}>
              <View style={styles.header}>
                <View style={styles.brand}>
                  <Image style={styles.logo} source={`${LYFT_LOGO_URL}?x-request=xhr`} />
                  <View style={styles.divider} />
                  <Text style={styles.stateText}>STANDARD 19PT</Text>
                </View>
                <Text style={styles.headerTitle}>VEHICLE INSPECTION</Text>
              </View>

              <View style={styles.driverSection}>
                <View style={styles.titleRow}>
                  <Text style={styles.driverTitle}>LYFT DRIVER INFO</Text>
                  <Text style={styles.mandatory}>ALL FIELDS ARE MANDATORY</Text>
                </View>
                <View style={styles.row}>
                  <Field
                    label="LYFT DRIVER NAME"
                    value={getFullName(certificateData)}
                    style={styles.wName}
                    handwritingStyle={handwritingStyle}
                  />
                  <Field
                    label="EMAIL ADDRESS"
                    value={certificateData?.Useremail}
                    style={[styles.wHalf, styles.noMargin].filter(Boolean)}
                    handwritingStyle={handwritingStyle}
                  />
                </View>
                <View style={styles.row}>
                  <Field
                    label="LICENSE PLATE #"
                    value={certificateData?.licensePlateNumber}
                    style={styles.wPlate}
                    handwritingStyle={handwritingStyle}
                  />
                  <VinField
                    value={certificateData?.vin}
                    handwritingStyle={handwritingStyle}
                  />
                </View>
                <View style={styles.row}>
                  <Field
                    label="VEHICLE MAKE"
                    value={certificateData?.make}
                    style={styles.wMake}
                    handwritingStyle={handwritingStyle}
                  />
                  <Field
                    label="VEHICLE MODEL"
                    value={certificateData?.model}
                    style={styles.wModel}
                    handwritingStyle={handwritingStyle}
                  />
                  <Field
                    label="VEHICLE YEAR"
                    value={certificateData?.year}
                    style={[styles.wYear, styles.noMargin].filter(Boolean)}
                    handwritingStyle={handwritingStyle}
                  />
                </View>
                <View style={styles.row}>
                  <Field
                    label="LYFT DRIVER SIGNATURE"
                    style={styles.wSignature}
                    handwritingStyle={handwritingStyle}
                  />
                  <Field
                    label="LYFT DRIVER PHONE NUMBER"
                    value={certificateData?.Userphone}
                    style={[styles.wPhone, styles.noMargin].filter(Boolean)}
                    handwritingStyle={handwritingStyle}
                  />
                </View>
              </View>

              <View style={styles.checklist}>
                <Text style={styles.checklistTitle}>
                  VEHICLE INSPECTION CHECKLIST
                </Text>
                <View style={styles.checklistGrid}>
                  {inspectionColumns.map((column, columnIndex) => (
                    <View
                      style={
                        columnIndex === 0
                          ? [styles.checkColumn, styles.leftColumn]
                          : styles.checkColumn
                      }
                      key={columnIndex}
                    >
                      <View style={styles.checkHeading}>
                        <Text style={styles.pointHeading}>INSPECTION POINT</Text>
                        <Text style={styles.statusHeading}>PASS</Text>
                        <Text style={styles.statusHeading}>FAIL</Text>
                      </View>
                      {column.map((item) => (
                        <InspectionItem
                          item={item}
                          checks={vehicleChecksArray}
                          key={item.number}
                        />
                      ))}

                      {columnIndex === 1 ? (
                        <View style={styles.resultBox}>
                          <Text style={styles.resultTitle}>
                            VEHICLE INSPECTION{" "}
                            <Text style={styles.resultCircleOne}>(Circle One)</Text>
                          </Text>
                          <ResultOption selected={finalStatus === "pass"}>
                            PASS
                          </ResultOption>
                          <ResultOption selected={finalStatus === "fail"}>
                            FAIL
                          </ResultOption>
                        </View>
                      ) : null}
                    </View>
                  ))}
                </View>
              </View>

              <View style={styles.inspectorSection}>
                <Text style={styles.inspectorTitle}>
                  TO BE COMPLETED BY INSPECTOR
                </Text>
                <View style={styles.row}>
                  <Field
                    label="COMPANY NAME"
                    value="Chex Inc."
                    style={styles.wHalf}
                    handwritingStyle={handwritingStyle}
                  />
                  <Field
                    label="INSPECTOR NAME"
                    value={getInspectorName(certificateData)}
                    style={[styles.wHalf, styles.noMargin].filter(Boolean)}
                    handwritingStyle={handwritingStyle}
                  />
                </View>
                <View style={styles.row}>
                  <Field
                    label="COMPANY/FACILITY ADDRESS"
                    value={certificateData?.stateAddress}
                    style={styles.wHalf}
                    handwritingStyle={handwritingStyle}
                  />
                  <Field
                    label="INSPECTION DATE (document expires one year from this date)"
                    value={formatDate(certificateData?.reviewedDate)}
                    style={[styles.wHalf, styles.noMargin].filter(Boolean)}
                    handwritingStyle={handwritingStyle}
                  />
                </View>
                <View style={[styles.field, styles.noMargin].filter(Boolean)}>
                  <View style={styles.fieldValue}>
                    {certificateData?.Inspectorsignature ? (
                      <Image
                        style={styles.signatureImage}
                        source={`${process.env.REACT_APP_AWS_S3_LINK}/${certificateData.Inspectorsignature}?x-request=xhr`}
                      />
                    ) : null}
                  </View>
                  <Text style={styles.fieldLabel}>INSPECTOR SIGNATURE</Text>
                </View>
              </View>
            </Page>
          </Document>
        }
        fileName={fileName}
      >
        {({ loading }) => (
          <>
            {loading ? (
              <div className="certificate-download-button is-loading" aria-live="polite">
                <span className="certificate-download-spinner" />
                <div>Preparing PDF</div>
              </div>
            ) : (
              <div className="certificate-download-button">
                <div>{linkLabel}</div>
                <i className="fa fa-download" />
              </div>
            )}
          </>
        )}
      </PDFDownloadLink>
    </div>
  );
}


const LyftCertificateArizonaDownLoadConnected = (props) => {
  const { id, companyId, reportData, checksData } = props;
  React.useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      props.getVehicleCertificate({ id, companyId }, props.setLoading, "template3");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);
  return (
    <div className="certificate-download-group">
      {!reportData ? (
        <div className="certificate-download-button is-loading" aria-live="polite">
          <span className="certificate-download-spinner" />
          <div>Loading certificate data</div>
        </div>
      ) : (
        getCertificateFontDownloads(reportData?.certificateFont).map((cfg) => (
          <LyftCertificateArizonaDownLoad
            key={cfg.handwritingFont}
            reportData={reportData} certificateData={reportData}
            checksData={checksData} checks={checksData} vehicleChecksArray={checksData} firstHalfData={checksData}
            handwritingFont={cfg.handwritingFont}
            fileName={cfg.fileName}
            linkLabel={cfg.linkLabel}
          />
        ))
      )}
    </div>
  );
};

function mapStateToProps(state) {
  const sliceData = state && state.vehicleInstruction && state.vehicleInstruction.lyftVehicleCertificate;
  return {
    reportData: sliceData,
    checksData: setLyftReportData(sliceData && sliceData.Files),
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(ActionCreators, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(LyftCertificateArizonaDownLoadConnected);
